import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DefaultLocalCartService } from '@asm/features/sync/services/cart/default-local-cart.service';

/**
 * Handler offline para operações de carrinho — atua como controller HTTP.
 * Extrai os dados do request e delega toda a lógica ao OfflineCartService.
 *
 * Cobre: POST /cart, POST /cart/get-or-create, GET /cart/{code}, POST /cart/add,
 *        PATCH /cart/{code}/entries/{n}, DELETE /cart/{code}/entries/{n},
 *        DELETE /cart/{code}/entries/{n}/promotions/{promotionCode},
 *        PATCH /cart/{code}, POST /cart/{code}/checkout
 */
@Injectable({ providedIn: 'root' })
export class CartOfflineHandler {
  private readonly cartService = inject(DefaultLocalCartService);

  canHandle(req: HttpRequest<unknown>): boolean {
    const { url, method } = req;
    return (
      (method === 'POST' && /\/cart$/.test(url)) ||
      (method === 'POST' && url.includes('/cart/get-or-create')) ||
      (method === 'GET' && /\/cart\/[^/]+$/.test(url) && !url.includes('/entries/')) ||
      (method === 'POST' && url.includes('/cart/add')) ||
      (method === 'PATCH' && /\/cart\/[^/]+\/entries\/\d+/.test(url)) ||
      (method === 'DELETE' && /\/cart\/[^/]+\/entries\/\d+\/promotions\/[^/]+/.test(url)) ||
      (method === 'DELETE' && /\/cart\/[^/]+\/entries\/\d+/.test(url) && !url.includes('/promotions/')) ||
      (method === 'PATCH' && /\/cart\/[^/]+$/.test(url) && !url.includes('/entries/')) ||
      (method === 'POST' && /\/cart\/[^/]+\/checkout/.test(url))
    );
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url, method } = req;
    const b2bUnitId = req.headers.get('x-b2bunit-id') ?? '';

    if (method === 'POST' && /\/cart$/.test(url)) {
      return new HttpResponse({ status: 200, body: await this.cartService.getOrCreate(b2bUnitId) });
    }

    if (method === 'POST' && url.includes('/cart/get-or-create')) {
      return new HttpResponse({ status: 200, body: await this.cartService.getOrCreate(b2bUnitId) });
    }

    if (method === 'GET' && /\/cart\/[^/]+$/.test(url) && !url.includes('/entries/')) {
      const cartCode = url.split('/cart/')[1];
      return new HttpResponse({ status: 200, body: await this.cartService.getCart(cartCode, b2bUnitId) });
    }

    if (method === 'POST' && url.includes('/cart/add')) {
      return new HttpResponse({ status: 200, body: await this.cartService.addEntry(req.body, b2bUnitId) });
    }

    if (method === 'PATCH' && /\/cart\/[^/]+\/entries\/\d+/.test(url)) {
      const entryNumber = Number(url.split('/entries/')[1]);
      return new HttpResponse({
        status: 200,
        body: await this.cartService.patchEntry(url, entryNumber, req.body, b2bUnitId),
      });
    }

    if (method === 'DELETE' && /\/cart\/[^/]+\/entries\/\d+\/promotions\/[^/]+/.test(url)) {
      const parts = url.match(/\/cart\/([^/]+)\/entries\/(\d+)\/promotions\/([^/?]+)/);
      const entryNumber = Number(parts?.[2]);
      const promotionCode = parts?.[3] ?? '';
      return new HttpResponse({
        status: 200,
        body: await this.cartService.removePromotion(url, entryNumber, promotionCode, b2bUnitId),
      });
    }

    if (method === 'DELETE' && /\/cart\/[^/]+\/entries\/\d+/.test(url) && !url.includes('/promotions/')) {
      const entryNumber = Number(url.split('/entries/')[1]);
      return new HttpResponse({
        status: 200,
        body: await this.cartService.deleteEntry(url, entryNumber, b2bUnitId),
      });
    }

    if (method === 'PATCH' && /\/cart\/[^/]+$/.test(url) && !url.includes('/entries/')) {
      return new HttpResponse({ status: 200, body: await this.cartService.patchCart(url, req.body, b2bUnitId) });
    }

    if (method === 'POST' && /\/cart\/[^/]+\/checkout/.test(url)) {
      return new HttpResponse({ status: 200, body: {} });
    }

    throw new Error(`[CartOfflineHandler] Rota não mapeada: ${method} ${url}`);
  }
}
